/** The workspace's left column: a compact, sortable queue for one title. */
import { useEffect, type ReactNode } from 'react'
import { stateLabel, topicLabel, topicTone, waitingLabel, waitingTone, type DeskConversation } from '../../lib/supportDesk'
import { SORT_OPTIONS, conversationParam, type DeskSort } from '../../lib/supportWorkspace'
import { deskOff } from '../../pages/support/SupportQueue'
import { useConversationPage } from './useConversationPage'

export const QUEUE_PAGE = 50
const TONE: Record<string, string> = { ok: 'text-paper-dim', soon: 'text-amber-glow', late: 'text-red-400' }
const SELECT = 'bg-ink-900 border border-ink-500 px-2 py-2 text-paper font-mono text-[12px]'

type Props = {
  params: URLSearchParams
  sort: DeskSort
  onSort: (s: DeskSort) => void
  offset: number
  onOffset: (n: number) => void
  selected: string | null
  onSelect: (c: DeskConversation) => void
  onFirst?: (c: DeskConversation | undefined) => void
}

export function QueueList({ params, sort, onSort, offset, onOffset, selected, onSelect, onFirst }: Props) {
  const list = useConversationPage(params, { poll: true })
  const items = list.data?.items ?? []
  const total = list.data?.total ?? 0
  const first = items[0]
  useEffect(() => { if (list.data) onFirst?.(first) }, [list.data, first, onFirst])
  // After an action empties the last page, step back to the last page that has rows.
  useEffect(() => {
    if (list.data && list.data.items.length === 0 && list.data.total > 0 && offset > 0) {
      onOffset(Math.floor((list.data.total - 1) / QUEUE_PAGE) * QUEUE_PAGE)
    }
  }, [list.data, offset, onOffset])

  return (
    <section aria-label="Queue" className="flex flex-col min-w-0 border-ink-500 xl:border-r">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-ink-500">
        <label htmlFor="queue-sort" className="label">Sort</label>
        <select id="queue-sort" className={`${SELECT} flex-1`} value={sort} onChange={e => onSort(e.target.value as DeskSort)}>
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {list.isLoading && <p className="px-3 py-3 text-paper-trace text-sm">Loading…</p>}
      {list.isError && !deskOff(list.error) && <p className="px-3 py-3 text-red-400 text-sm">Couldn't load conversations. Try again.</p>}
      {list.data && items.length === 0 && <p className="px-3 py-3 text-paper-trace text-sm">Nothing here.</p>}

      <ul>
        {items.map(c => {
          const key = conversationParam(c.channel, c.message_id)
          const on = key === selected
          const tone = c.state === 'to_review' ? waitingTone(c.waiting_hours, c.target_hours) : 'ok'
          return (
            <li key={key}>
              <button type="button" onClick={() => onSelect(c)} aria-current={on ? 'true' : undefined}
                className={`w-full text-left flex flex-col gap-1 px-3 py-3 border-b border-ink-500 border-l-2 hover:bg-ink-800 ${on ? 'bg-ink-800 border-l-amber-glow' : 'border-l-transparent'}`}>
                <span className="flex justify-between items-baseline gap-2">
                  <span className="text-paper truncate">{c.name || c.email || 'Unknown'}</span>
                  <span className={`font-mono text-[11px] ${TONE[tone]}`}>{waitingLabel(c.waiting_hours)}</span>
                </span>
                <span className="text-paper-dim text-[13px] truncate">{c.snippet}</span>
                <span className="flex flex-wrap gap-1">
                  <Chip>{c.channel}</Chip>
                  <Chip tone={topicTone(c.topic)}>{topicLabel(c.topic)}</Chip>
                  {c.state !== 'to_review' && <Chip>{stateLabel(c.state)}</Chip>}
                  {c.member && <Chip>Member</Chip>}
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      {total > QUEUE_PAGE && (
        <div className="flex gap-3 items-center px-3 py-2">
          <button className="label hover:underline disabled:opacity-40" disabled={offset === 0} onClick={() => onOffset(Math.max(0, offset - QUEUE_PAGE))}>Previous</button>
          <span className="label">{offset + 1}–{Math.min(offset + QUEUE_PAGE, total)} of {total}</span>
          <button className="label hover:underline disabled:opacity-40" disabled={offset + QUEUE_PAGE >= total} onClick={() => onOffset(offset + QUEUE_PAGE)}>Next</button>
        </div>
      )}
    </section>
  )
}

export function Chip({ children, tone }: { children: ReactNode; tone?: string }) {
  return (
    <span className={`font-mono text-[10px] uppercase tracking-tight-cap px-1.5 py-0.5 border rounded-sm whitespace-nowrap ${tone ?? 'text-paper-dim border-ink-500'}`}>
      {children}
    </span>
  )
}
